import React from 'react';
import { t } from '@lingui/macro';
import { createStackNavigator } from '@react-navigation/stack';

import PrimeLogoTitle from '@src/components/PrimeLogoTitle';
import SearchBar from '@src/components/SearchBar';
import { CheckAccess } from '@src/helpers/hasAccessMember';
import useActiveOnRegions from '@src/hooks/useActiveOnRegions';
import useAllStatus from '@src/hooks/useAllStatus';
import useHasPrime from '@src/hooks/useHasPrime';
import useStackScreenOptions from '@src/hooks/useStackScreenOptions';
import { i18n } from '@src/i18n';
import CampsScreen from '@src/screens/Camps';
import CollectionScreen from '@src/screens/Collection';
import DummyScreen from '@src/screens/Dummy';
import HashtagScreen from '@src/screens/Hashtag';
import ImmigrationBookScreen from '@src/screens/ImmigrationBook';
import ImmigrationBookAudioReaderScreen from '@src/screens/ImmigrationBookAudioReader';
import ImmigrationBookMenuScreen from '@src/screens/ImmigrationBookMenu';
import ImmigrationBooksScreen from '@src/screens/ImmigrationBooks';
import ImmigrationHomeScreen from '@src/screens/ImmigrationHome';
import ImmigrationPlansScreen from '@src/screens/ImmigrationPlans';
import JourneyScreen from '@src/screens/Journey';
import JourneysScreen from '@src/screens/Journeys';
import LocationScreen from '@src/screens/Location';
import NewPostScreen from '@src/screens/NewPost';
import PickLocationScreen from '@src/screens/PickLocation';
import PostScreen from '@src/screens/Post';
import PostsScreen from '@src/screens/Posts';
import PrimeBookScreen from '@src/screens/PrimeBook';
import PrimeBookAudioReaderScreen from '@src/screens/PrimeBookAudioReader';
import PrimeBookMenuScreen from '@src/screens/PrimeBookMenu';
import PrimeBooksScreen from '@src/screens/PrimeBooks';
// import ImmigrationBooksScreen from '@src/screens/ImmigrationBooks';
import PrimeBookTextReaderScreen from '@src/screens/PrimeBookTextReader';
import PrimeHomeScreen from '@src/screens/PrimeHome';
import PrimeLibraryScreen from '@src/screens/PrimeLibrary';
import PrimePlansScreen from '@src/screens/PrimePlans';
import PrimeTripAdvisorScreen from '@src/screens/PrimeTripAdvisor';
import ProfileScreen from '@src/screens/Profile';
import ProfileEditScreen from '@src/screens/ProfileEdit';
import ProfileFollowersScreen from '@src/screens/ProfileFollowers';
import ProfileFollowingsScreen from '@src/screens/ProfileFollowings';
import SearchScreen from '@src/screens/Search';
import SupportScreen from '@src/screens/Support';
import TopJourneysScreen from '@src/screens/TopJourneys';
import TourScreen from '@src/screens/Tour';
import ToursScreen from '@src/screens/Tours';
import UsersScreen from '@src/screens/Users';
import WalletScreen from '@src/screens/Wallet';

import Drawer from './Drawer';
import { AuthenticatedRootStackParamList } from './types';

const Stack = createStackNavigator<AuthenticatedRootStackParamList>();

const Component: React.FC = () => {
  const stackScreenOptions = useStackScreenOptions();
  const hasPrime = CheckAccess(useAllStatus()) > 0;
  const regions = useActiveOnRegions('IR');

  return (
    <Stack.Navigator
      mode="card"
      headerMode="screen"
      screenOptions={stackScreenOptions}
    >
      <Stack.Screen
        name="Drawer"
        component={Drawer}
        options={{
          headerShown: false,
        }}
      />
      {regions && (
        <>
          <Stack.Screen
            name="PrimePlans"
            component={PrimePlansScreen}
            options={{
              title: `${i18n._(t`Buy Prime Subscription`)}`,
              headerTitle: i18n._(t`Buy Prime Subscription`),
            }}
          />
          <Stack.Screen
            name="ImmigrationPlans"
            component={ImmigrationPlansScreen}
            options={{
              title: `${i18n._(t`Buy Immigration Subscription`)}`,
              headerTitle: i18n._(t`Buy Immigration Subscription`),
            }}
          />

          <Stack.Screen
            name="PrimeHome"
            component={PrimeHomeScreen}
            options={{
              title: `${i18n._(t`Prime`)} - ${i18n._(t`Youtopin`)}`,
              headerTitle: () => <PrimeLogoTitle />,
              headerTitleAlign: 'center',
              headerBackTitle: '',
              headerTruncatedBackTitle: '',
            }}
          />
          <Stack.Screen
            name="ImmigrationHome"
            component={ImmigrationHomeScreen}
            options={{
              title: `${i18n._(t`Immigration`)} - ${i18n._(t`Youtopin`)}`,
              headerTitle: () => <PrimeLogoTitle />,
              headerTitleAlign: 'center',
              headerBackTitle: '',
              headerTruncatedBackTitle: '',
            }}
          />
          <Stack.Screen name="PrimeBooks" component={PrimeBooksScreen} />
          <Stack.Screen name="PrimeBook" component={PrimeBookScreen} />
          <Stack.Screen
            name="ImmigrationBooks"
            component={ImmigrationBooksScreen}
          />
          <Stack.Screen
            name="ImmigrationBook"
            component={ImmigrationBookScreen}
          />
          {hasPrime && (
            <>
              <Stack.Screen
                name="PrimeLibrary"
                component={PrimeLibraryScreen}
                options={{
                  title: `${i18n._(t`My Library`)} - ${i18n._(t`Youtopin`)}`,
                  headerTitle: i18n._(t`My Library`),
                  headerTitleAlign: 'center',
                }}
              />
              <Stack.Screen
                name="PrimeTripAdvisor"
                component={PrimeTripAdvisorScreen}
                options={{
                  title: `${i18n._(t`Trip Advisor`)} - ${i18n._(t`Youtopin`)}`,
                  headerTitle: i18n._(t`Trip Advisor`),
                  headerTitleAlign: 'center',
                }}
              />
              <Stack.Screen
                name="PrimeBookAudioReader"
                component={PrimeBookAudioReaderScreen}
                options={{
                  headerTitle: '',
                  title: `${i18n._(t`Prime Book Reader`)} - ${i18n._(
                    t`Youtopin`
                  )}`,
                  headerTitleAlign: 'center',
                }}
              />
              <Stack.Screen
                name="ImmigrationBookAudioReader"
                component={ImmigrationBookAudioReaderScreen}
                options={{
                  headerTitle: '',
                  title: `${i18n._(t`Prime Book Reader`)} - ${i18n._(
                    t`Youtopin`
                  )}`,
                  headerTitleAlign: 'center',
                }}
              />
              <Stack.Screen
                name="PrimeBookTextReader"
                component={PrimeBookTextReaderScreen}
                options={{
                  headerTitle: '',
                  title: `${i18n._(t`Prime Book Reader`)} - ${i18n._(
                    t`Youtopin`
                  )}`,
                }}
              />
              <Stack.Screen
                name="PrimeBookMenu"
                component={PrimeBookMenuScreen}
                options={{
                  title: `${i18n._(t`Prime Book Menu`)} - ${i18n._(
                    t`Youtopin`
                  )}`,
                  headerTitleAlign: 'center',
                }}
              />
              <Stack.Screen
                name="ImmigrationBookMenu"
                component={ImmigrationBookMenuScreen}
                options={{
                  title: `${i18n._(t`Immigration Book Menu`)} - ${i18n._(
                    t`Youtopin`
                  )}`,
                  headerTitleAlign: 'center',
                }}
              />
            </>
          )}
        </>
      )}
      <Stack.Screen
        name="TopJourneys"
        component={TopJourneysScreen}
        options={{
          title: `${i18n._(t`Top Journeys`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Top Journeys`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Journey"
        component={JourneyScreen}
        options={{
          headerShown: false,
          title: `${i18n._(t`Journey`)} - ${i18n._(t`Youtopin`)}`,
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Post"
        component={PostScreen}
        options={{
          title: `${i18n._(t`Post`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Post`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Wallet"
        component={WalletScreen}
        options={{
          title: `${i18n._(t`Wallet`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Wallet`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="NewPost"
        component={NewPostScreen}
        options={{
          title: `${i18n._(t`New Post`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`New Post`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="PickLocation"
        component={PickLocationScreen}
        options={{
          title: `${i18n._(t`Pick Location`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Pick Location`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Tour"
        component={TourScreen}
        options={{
          title: `${i18n._(t`Tour information`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: '',
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Tours"
        component={ToursScreen}
        options={{
          title: `${i18n._(t`Tours`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Tours`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Collection"
        component={CollectionScreen}
        options={{
          title: `${i18n._(t`Collection`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Collection`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Profile"
        component={ProfileScreen}
        options={{
          title: `${i18n._(t`Profile`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Profile`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Location"
        component={LocationScreen}
        options={{
          title: `${i18n._(t`Location`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Location`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Camps"
        component={CampsScreen}
        options={{
          title: `${i18n._(t`Camps`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Camps`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="ProfileFollowers"
        component={ProfileFollowersScreen}
        options={{
          title: `${i18n._(t`Followers`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Followers`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="ProfileFollowings"
        component={ProfileFollowingsScreen}
        options={{
          title: `${i18n._(t`Followings`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Followings`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="ProfileEdit"
        component={ProfileEditScreen}
        options={{
          title: `${i18n._(t`Edit Profile`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Edit Profile`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Search"
        component={SearchScreen}
        options={{
          title: `${i18n._(t`Search`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: () => <SearchBar />,
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Hashtag"
        component={HashtagScreen}
        options={{
          title: `${i18n._(t`Hashtag`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Hashtag`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Support"
        component={SupportScreen}
        options={{
          title: `${i18n._(t`Support`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Support`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Posts"
        component={PostsScreen}
        options={{
          title: `${i18n._(t`Posts`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Posts`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Users"
        component={UsersScreen}
        options={{
          title: `${i18n._(t`Users`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Users`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Journeys"
        component={JourneysScreen}
        options={{
          title: `${i18n._(t`Journeys`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Journeys`),
          headerTitleAlign: 'center',
        }}
      />
      <Stack.Screen
        name="Dummy"
        component={DummyScreen}
        options={{
          title: `${i18n._(t`Explore`)} - ${i18n._(t`Youtopin`)}`,
          headerTitle: i18n._(t`Explore`),
          headerTitleAlign: 'center',
        }}
      />
    </Stack.Navigator>
  );
};

Component.displayName = 'AuthenticatedRootStack';

export default Component;
